// src/router/index.ts

import { createRouter, createWebHistory } from "vue-router";
import type {
  RouteRecordRaw,
  RouteLocationNormalized,
  RouteMeta,
} from "vue-router";
import { systemRoutes, getSystemConfig, getSystems } from "./systems";
import { jwtService } from "@/services/jwt";
import { cookieService } from "@/services/cookieService";
import { errorRecoveryService } from "@/services/errorRecoveryService";
import { decodeJumpUrl } from "@/utils/url";
import { reportError } from "@/utils/sentry";
import { getJumpUrl } from "@/api/auth";
import { isWechatEnvironment, isWechatDevtools } from "@/api/wechat";
import { pathEquals, pathInList } from "@/utils/path";

// 辅助函数：检查是否为公开页面
const isPublicRoute = (path: string, routeMeta?: RouteMeta): boolean => {
  const publicPages = [
    "/login",
    "/wechat-authorize",
    "/wechat-rebind",
    "/password/forgot",
    "/password/verify",
    "/password/new",
    "/status",
    "/about",
    "/wechat-debug",
  ];

  const isSystemLoginPage = routeMeta?.name?.toString().includes("-login");
  return (
    pathInList(path, publicPages) || !!isSystemLoginPage || !!routeMeta?.public
  );
};

// 辅助函数：构建登录跳转
const buildLoginRedirect = (
  to: RouteLocationNormalized,
  loginPath: string = "/login",
) => {
  return {
    path: loginPath,
    query: {
      redirect: to.fullPath,
      ...to.query,
    },
  };
};

// 辅助函数：处理微信环境自动跳转
const handleWechatAutoRedirect = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
): RouteLocationNormalized | undefined => {
  const token = cookieService.get("jwt_token");
  if (pathEquals(to.path, "/login") && !token) {
    const isInWechat = isWechatEnvironment() || isWechatDevtools();
    const isFromWechatAuth =
      to.query.wechat_openid ||
      to.query.from_wechat_auth === "true" ||
      from.path === "/wechat-authorize";
    const isManualLogout = to.query.manual_logout === "true";

    if (isInWechat && !isFromWechatAuth && !isManualLogout) {
      console.log("Redirecting to WeChat authorization from login page", {
        isInWechat,
        isFromWechatAuth,
        isManualLogout,
        fromPath: from.path,
        query: to.query,
      });
      return {
        path: "/wechat-authorize",
        query: to.query,
      } as RouteLocationNormalized;
    }
  }
  return undefined;
};

const routes: RouteRecordRaw[] = [
  {
    path: "/",
    name: "root",
    redirect: (to) => {
      const targetSystem = to.query.system as string;
      const systems = getSystems();
      if (targetSystem && systems[targetSystem]) {
        return {
          path: systems[targetSystem].path,
          query: to.query,
        };
      }
      return "/login";
    },
  },
  {
    path: "/login",
    alias: "/login.html",
    name: "login",
    component: () => import("@/views/login/LoginView.vue"),
    meta: {
      title: "登录",
      public: true,
    },
  },
  {
    path: "/wechat-authorize",
    alias: "/wechat-authorize.html",
    name: "wechat-authorize",
    component: () => import("@/views/authorize/WechatAuthorizeView.vue"),
    meta: {
      title: "微信授权登录",
      public: true,
    },
  },
  {
    path: "/wechat-rebind",
    name: "wechat-rebind",
    component: () => import("@/views/wechat/WechatRebindView.vue"),
    meta: {
      title: "微信换绑",
      public: true,
    },
  },
  {
    path: "/authorize",
    alias: "/authorize.html",
    name: "authorize",
    component: () => import("@/views/authorize/AuthorizeView.vue"),
    meta: {
      title: "授权",
      requiresAuth: true,
    },
  },
  {
    path: "/email",
    alias: "/email.html",
    name: "email",
    component: () => import("@/views/email/EmailView.vue"),
    meta: {
      title: "邮箱设置",
      requiresAuth: true,
    },
  },
  {
    path: "/phone",
    alias: "/phone.html",
    name: "phone",
    component: () => import("@/views/phone/PhoneView.vue"),
    meta: {
      title: "手机号设置",
      requiresAuth: true,
    },
  },
  // 密码相关路由
  {
    path: "/password",
    children: [
      {
        path: "forgot",
        name: "password-forgot",
        component: () => import("@/views/password/ForgotPasswordView.vue"),
        meta: {
          title: "忘记密码",
          public: true,
        },
      },
      {
        path: "verify",
        name: "password-verify",
        component: () => import("@/views/password/VerifyPasswordView.vue"),
        meta: {
          title: "验证重置",
          public: true,
        },
      },
      {
        path: "modify",
        name: "password-modify",
        component: () => import("@/views/password/ModifyPasswordView.vue"),
        meta: {
          title: "修改密码",
          requiresAuth: true,
        },
      },
    ],
  },
  // Passkey相关路由
  {
    path: "/passkey",
    children: [
      {
        path: "login",
        name: "passkey-login",
        component: () => import("@/views/passkey/PasskeyLoginView.vue"),
        meta: {
          title: "Passkey登录",
          public: true,
        },
      },
      {
        path: "register",
        name: "passkey-register",
        component: () => import("@/views/passkey/PasskeyRegisterView.vue"),
        meta: {
          title: "Passkey注册",
          requiresAuth: true,
        },
      },
    ],
  },
  {
    path: "/status",
    name: "status",
    component: () => import("@/views/status/SystemStatusView.vue"),
    meta: {
      title: "系统状态",
      public: true,
    },
  },
  {
    path: "/about",
    name: "about",
    component: () => import("@/views/About.vue"),
    meta: {
      title: "关于我们",
      public: true,
    },
  },
  {
    path: "/wechat-debug",
    name: "wechat-debug",
    component: () => import("@/views/debug/WechatDebugView.vue"),
    meta: {
      title: "微信调试",
      public: true,
    },
  },
  {
    path: "/security-center",
    name: "security-center",
    component: () => import("@/views/security/SecurityCenterView.vue"),
    meta: {
      title: "账户安全中心",
      requiresAuth: true,
    },
  },
  ...systemRoutes,
  // 添加 404 捕获路由，必须放在最后
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    redirect: "/",
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_BASE_URL),
  routes,
});

router.beforeEach(async (to, from, next) => {
  try {
    // 设置页面标题
    document.title = `${to.meta.title ? to.meta.title + " - " : ""}${import.meta.env.VITE_APP_TITLE}`;

    // 检测是否有被困用户情况
    if (errorRecoveryService.detectTrappedUser()) {
      console.warn("Detected trapped user, initiating recovery");
      await errorRecoveryService.forceRecovery({
        reason: "router_guard_detection",
        redirectPath: "/login",
      });
      return;
    }

    // 处理 jump_to 参数（外部直接跳转）
    const jumpTo = to.query.jump_to as string;
    if (jumpTo) {
      const decodedUrl = decodeJumpUrl(jumpTo);
      if (decodedUrl) {
        const hasValidTokens = await jwtService.ensureValidTokens();
        if (hasValidTokens) {
          // token 有效，直接跳转到目标 URL
          window.location.href = decodedUrl;
          return;
        }

        // token 无效或不存在，重定向到登录页保存跳转目标
        if (to.path !== "/login") {
          return next({
            path: "/login",
            query: {
              jump_to: jumpTo,
              ...to.query,
            },
          });
        }
      }
    }

    // 处理 jump_alias 参数（系统别名跳转）
    const jumpAlias = to.query.jump_alias as string;
    if (jumpAlias) {
      const hasValidTokens = await jwtService.ensureValidTokens();
      if (hasValidTokens) {
        try {
          const response = await getJumpUrl(jumpAlias);
          if (response?.url) {
            window.location.href = response.url;
            return;
          }
        } catch (error) {
          console.error("Failed to get jump URL:", error);
          reportError(
            error instanceof Error ? error : new Error(String(error)),
            {
              component: "RouterGuard",
              action: "getJumpUrl",
              alias: jumpAlias,
            },
          );
        }
      }

      // 未登录或获取URL失败，重定向到登录页
      if (to.path !== "/login") {
        return next({
          path: "/login",
          query: {
            jump_alias: jumpAlias,
            ...to.query,
          },
        });
      }
    }

    // 标准化路径：移除 .html 后缀和末尾斜杠
    let path = to.path.replace(/\.html$/, "");

    // 标准化末尾斜杠（除根路径外）
    if (path !== "/" && path.endsWith("/")) {
      path = path.slice(0, -1);
    }

    // 如果路径被修改，重定向到标准化路径
    if (to.path !== path) {
      console.log(`🔄 Path normalization: ${to.path} → ${path}`);
      const resolvedRoute = router.resolve({
        path,
        query: to.query,
        hash: to.hash,
      });
      return next(resolvedRoute);
    }

    // 检查路由类型
    const isPublic = isPublicRoute(path, to.meta);
    const requiresAuth = !isPublic;

    // 公开页面处理
    if (isPublic) {
      // 微信环境自动跳转处理
      const wechatRedirect = handleWechatAutoRedirect(to, from);
      if (wechatRedirect) {
        return next(wechatRedirect);
      }

      // 如果是登录页且已登录，处理跳转逻辑
      if (path === "/login" || to.name?.toString().includes("-login")) {
        const hasValidTokens = await jwtService.ensureValidTokens();
        if (hasValidTokens) {
          // 处理jump_to跳转
          if (jumpTo) {
            const decodedUrl = decodeJumpUrl(jumpTo);
            if (decodedUrl) {
              window.location.href = decodedUrl;
              return;
            }
          }

          // 已登录用户，重定向到适当页面
          console.log("Already logged in, redirecting", {
            path,
            isSystemLoginPage: to.name?.toString().includes("-login"),
          });

          const systemConfig = to.meta.system || getSystemConfig(path);
          const targetPath = systemConfig ? systemConfig.path : "/authorize";
          return next({
            path: targetPath,
            query: to.query,
          });
        }
      }

      return next();
    }

    // 需要认证的页面处理
    if (requiresAuth) {
      const hasValidTokens = await jwtService.ensureValidTokens();

      if (!hasValidTokens) {
        console.log("Authentication required, redirecting to login", {
          path,
          to: to.fullPath,
        });

        // 清理无效的认证状态
        jwtService.clearTokens();

        // 确定正确的登录页面
        const systemConfig = to.meta.system || getSystemConfig(path);
        const loginPath = systemConfig
          ? `${systemConfig.path}/login`
          : "/login";

        return next(buildLoginRedirect(to, loginPath));
      }
    }

    // 正常通过
    next();
  } catch (error) {
    console.error("Router guard error:", error);
    reportError(error instanceof Error ? error : new Error(String(error)), {
      component: "RouterGuard",
      path: to.path,
      action: "beforeEach",
    });

    // 错误恢复：尝试强制恢复
    try {
      await errorRecoveryService.forceRecovery({
        reason: "router_guard_error",
        redirectPath: "/login",
        showMessage: false,
      });
    } catch (recoveryError) {
      console.error("Recovery failed:", recoveryError);

      // 最后的兜底：清理状态并跳转到登录页
      jwtService.clearTokens();
      next(buildLoginRedirect(to));
    }
  }
});

export default router;
